import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useHttp } from '../hooks/http.hook';

// MUI
import {
  AppBar as MuiAppBar,
  Badge,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  InputBase,
  List,
  Paper,
  Stack,
  Skeleton,
  Toolbar,
  Typography,
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

// Your existing components
import MainListItemsComponents from '../components/MainListItemsComponents';
import EquipmentsTableComponent from '../components/EquipmentsTableComponent';
import EquipmentsCreateComponents from '../components/EquipmentsCreateComponents';
import EquipmentsGroupTableComponents from '../components/EquipmentsGroupTableComponents';
import EquipmentsGroupCreateComponents from '../components/EquipmentsGroupCreateComponents';
import EquipmentsPostsTableComponents from '../components/EquipmentPostsTableComponents';
import EquipmentsPostsCreateComponents from '../components/EquipmentsPostsCreateComponents';
import DocumentsTableComponents from '../components/DocumentsTableComponents';
import DocumentsCreateComponents from '../components/DocumentsCreateComponents';
import DocumentsPostTableComponents from '../components/DocumentsPostTableComponents';
import DocumentsPostCreateComponents from '../components/DocumentsPostCreateComponents';
import SolutionsTableComponent from '../components/SolutionsTableComponent';
import SolutionsCreateComponents from '../components/SolutionsCreateComponents';
import CompanyTableComponent from '../components/CompanyTableComponent';
import CompanyCreateComponents from '../components/CompanyCreateComponents';
import SupportsTableComponent from '../components/SupportsTableComponent';
import SupportCreateComponents from '../components/SupportCreateComponents';
import ReferenceGroupTableComponent from '../components/ReferenceGroupTableComponent';
import ReferenceGroupCreateComponents from '../components/ReferenceGroupCreateComponents';
import ReferencePostTableComponents from '../components/ReferencePostTableComponents';
import ReferencePostCreateComponents from '../components/ReferencePostCreateComponents';
import DocumentsSupportsPostTableComponents from '../components/DocumentsSupportsPostTableComponents';
import DocumentsSupportsPostCreateComponents from '../components/DocumentsSupportsPostCreateComponents';
import DocumentsSolutionsPostTableComponents from '../components/DocumentsSolutionsPostTableComponents';
import DocumentsSolutionsPostCreateComponents from '../components/DocumentsSolutionsPostCreateComponents';
import DocumentsCompanyPostTableComponents from '../components/DocumentsCompanyPostTableComponents';
import DocumentsCompanyPostCreateComponents from '../components/DocumentsCompanyPostCreateComponents';

// ----------------------
// THEME with dark-mode toggle
// ----------------------
const ColorModeContext = React.createContext({ toggle: () => { } });

function ThemedApp({ children }) {
  const [mode, setMode] = useState('light');
  const colorMode = useMemo(() => ({ toggle: () => setMode(m => (m === 'light' ? 'dark' : 'light')) }), []);

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: '#2563eb' },
      secondary: { main: '#22c55e' },
      background: {
        default: mode === 'light' ? '#f7f7fb' : '#0b1020',
        paper: mode === 'light' ? '#ffffff' : '#111831',
      },
    },
    shape: { borderRadius: 16 },
    typography: {
      fontFamily: ['Inter', 'Roboto', 'system-ui', '-apple-system', 'Segoe UI', 'Arial'].join(','),
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: '1px solid', borderColor: mode === 'light' ? '#eee' : '#1f2947' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backdropFilter: 'saturate(120%) blur(8px)', background: mode === 'light' ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.75)' },
        },
      },
      MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 10, margin: '4px 8px' } } },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 12 } } },
    },
  }), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// ----------------------
// LAYOUT (Mini-drawer + polished AppBar)
// ----------------------
const drawerWidth = 260;

const AppBar = styled(MuiAppBar, { shouldForwardProp: (p) => p !== 'open' })(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], { duration: theme.transitions.duration.standard }),
  ...(open && { marginLeft: drawerWidth, width: `calc(100% - ${drawerWidth}px)` }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (p) => p !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'fixed',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', { duration: theme.transitions.duration.standard }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      width: 72,
    }),
  },
}));

function Section({ title, actions, children, loading, empty, height }) {
  return (
    <Paper sx={{ p: 2, width: '100%', transition: 'box-shadow .2s', '&:hover': { boxShadow: 6 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {loading ? (
        <Box>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Box>
      ) : empty ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>Здесь пока пусто</Typography>
          <Typography variant="body2">Добавьте первую запись с помощью кнопки выше</Typography>
        </Box>
      ) : (
        <Box sx={{ minHeight: height ?? 160 }}>{children}</Box>
      )}
    </Paper>
  );
}

function AdminLayout({ title, onLogout, nav, children, onSearch }) {
  const [open, setOpen] = useState(true);
  const mode = React.useContext(ColorModeContext);
  const [q, setQ] = useState('');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" open={open} color="transparent" elevation={0}>
        <Toolbar sx={{ gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={() => setOpen(v => !v)}>
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 0 }}>{title}</Typography>


          <IconButton color="inherit" onClick={mode.toggle} aria-label="toggle theme">
            <Badge>
              {/* показываем состояние темы через иконку */}
              <DarkModeIcon sx={{ display: { xs: 'none', md: 'block' } }} />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <Toolbar />
        <Divider />
        <List component="nav" sx={{ pt: 1 }}>
          {nav}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, ml: open ? `${drawerWidth}px` : '72px', transition: 'margin .2s ease' }}>
        <Toolbar />
        <Box sx={{ width: '100%', py: 3, px: { xs: 2, md: 4 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// ----------------------
// ADMIN PAGE (refactored)
// ----------------------
export default function AdminPage() {
  const { token, logout } = useContext(AuthContext);
  const { request } = useHttp();

  const [menuSelected, setMenuSelected] = useState(1);
  const [updateTable, setUpdateTable] = useState(null);

  // Shared selections
  const [making, setMaking] = useState(null);
  const [makingSelectRow, setMakingSelectRow] = useState(null);

  const [equipmentsGroupRows, setEquipmentsGroupRows] = useState(null);
  const [selectEquipmentsGroupRows, setSelectEquipmentsGroupRows] = useState(null);

  const [equipmentsPostRows, setEquipmentsPostRows] = useState(null);
  const [selectEquipmentsPostRows, setSelectEquipmentsPostRows] = useState(null);

  const [documentsRows, setDocumentsRows] = useState(null);
  const [selectDocumentsRows, setSelectDocumentsRows] = useState(null);

  const [documentsPostRows, setDocumentsPostRows] = useState(null);
  const [selectDocumentsPostRows, setSelectDocumentsPostRows] = useState(null);

  const [solutionsRows, setSolutionsRows] = useState(null);
  const [selectSolutionsRows, setSelectSolutionsRows] = useState(null);
  const [documentsSolutionsPostRows, setDocumentsSolutionsPostRows] = useState(null);
  const [selectDocumentsSolutionsPostRows, setSelectDocumentsSolutionsPostRows] = useState(null);

  const [companyRows, setCompanyRows] = useState(null);
  const [selectCompanyRow, setSelectCompanyRow] = useState(null);
  const [documentsCompanyPostRows, setDocumentsCompanyPostRows] = useState(null);
  const [selectDocumentsCompanyPostRows, setSelectDocumentsCompanyPostRows] = useState(null);

  const [supportRows, setSupportRows] = useState(null);
  const [selectSupportRow, setSelectSupportRow] = useState(null);
  const [documentsSupportsPostRows, setDocumentsSupportsPostRows] = useState(null);
  const [selectDocumentsSupportsPostRows, setSelectDocumentsSupportsPostRows] = useState(null);

  const [referencesGroupRows, setReferencesGroupRows] = useState(null);
  const [selectReferencesGroupRow, setSelectReferencesGroupRow] = useState(null);
  const [referencesPostRows, setReferencesPostRows] = useState(null);
  const [selectReferencesPostRow, setSelectReferencesPostRow] = useState(null);


  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ---------------
  // API calls
  // ---------------
  const authHeader = useMemo(() => ({ authorization: `${token}` }), [token]);

  const getMaking = async () => {
    const data = await request('/api/equipments/', 'GET', null, authHeader);
    setMaking(data);
  };
  const getEquipmentsPosts = async () => {
    const data = await request('/api/equipments_posts/', 'GET', null, authHeader);
    setEquipmentsPostRows(data);
  };
  const getEquipmentGroup = async () => {
    const data = await request('/api/equipments_groups/', 'GET', null, authHeader);
    setEquipmentsGroupRows(data);
  };
  const getDocuments = async () => {
    const data = await request('/api/documents/', 'GET', null, authHeader);
    setDocumentsRows(data);
  };
  const getDocumentsPost = async () => {
    const data = await request('/api/equipments_posts_documents/', 'GET', null, authHeader);
    setDocumentsPostRows(data);
  };
  const getDocumentsSupportsPost = async () => {
    const data = await request('/api/supports_posts_documents/', 'GET', null, authHeader);
    setDocumentsSupportsPostRows(data);
  };
  const getDocumentsCompanyPost = async () => {
    const data = await request('/api/companys_posts_documents/', 'GET', null, authHeader);
    setDocumentsCompanyPostRows(data);
  };
  const getDocumentsSolutionsPost = async () => {
    const data = await request('/api/solutions_posts_documents/', 'GET', null, authHeader);
    setDocumentsSolutionsPostRows(data);
  };
  const getSolutions = async () => {
    const data = await request('/api/solutions_posts/', 'GET', null, authHeader);
    setSolutionsRows(data);
  };
  const getCompany = async () => {
    const data = await request('/api/companys_posts/', 'GET', null, authHeader);
    setCompanyRows(data);
  };
  const getSupport = async () => {
    const data = await request('/api/supports_posts/', 'GET', null, authHeader);
    setSupportRows(data);
  };
  const getReferenceGroup = async () => {
    const data = await request('/api/references_groups/', 'GET', null, authHeader);
    setReferencesGroupRows(data);
  };
  const getReferencePost = async () => {
    const data = await request('/api/references_posts/', 'GET', null, authHeader);
    setReferencesPostRows(data);
  };

  // initial
  useEffect(() => {
    // preload if нужно
  }, []);

  // refresh section on menu change
  useEffect(() => {
    // сбрасываем выделения при переключении
    setSelectDocumentsPostRows(null);
    setSelectDocumentsRows(null);
    setSelectDocumentsSolutionsPostRows(null);
    setSelectDocumentsSupportsPostRows(null);
    setSelectDocumentsCompanyPostRows(null);
    setSelectEquipmentsPostRows(null);
    setSelectEquipmentsGroupRows(null);
    setMakingSelectRow(null);
    setSelectCompanyRow(null);
    setSelectSolutionsRows(null);
    setSelectSupportRow(null);
    setSelectReferencesGroupRow(null);
    setSelectReferencesPostRow(null);

    if (menuSelected === 1) getMaking();
    else if (menuSelected === 2) getSolutions();
    else if (menuSelected === 3) getCompany();
    else if (menuSelected === 4) getSupport();
    else if (menuSelected === 6) getEquipmentGroup();
    else if (menuSelected === 7) getEquipmentsPosts();
    else if (menuSelected === 8) getDocuments();
    else if (menuSelected === 9) getDocumentsPost();
    else if (menuSelected === 5) getReferenceGroup();
    else if (menuSelected === 10) getReferencePost();
    else if (menuSelected === 11) getDocumentsSolutionsPost();
    else if (menuSelected === 12) getDocumentsCompanyPost();
    else if (menuSelected === 13) getDocumentsSupportsPost();
  }, [menuSelected]);

  // update hook
  useEffect(() => {
    if (updateTable) {
      setMakingSelectRow(null);
      setUpdateTable(0);
    }
  }, [updateTable]);

  // helpers
  const isLoading = (data) => data === null;
  const isEmpty = (data) => Array.isArray(data) && data.length === 0;
  const bottomRef = React.useRef(null);

  return (
    <ThemedApp>
      <AdminLayout
        title="Панель администратора"
        onLogout={logout}
        nav={<MainListItemsComponents selected={setMenuSelected} />}
        onSearch={(q) => { /* при желании фильтруй таблицы */ }}
      >
        {/* Оборудование */}
        {menuSelected === 1 && (
          <Stack spacing={3}>
              <Section
                title="Оборудование"
                actions={
                  <>
                    <Button startIcon={<RefreshIcon />} size="small" onClick={getMaking}>Обновить</Button>
                    <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                  </>
                }
                loading={isLoading(making)}
                empty={isEmpty(making)}
                height={240}
              >
                <EquipmentsTableComponent headers={["Заголовок", "Текст", "Подробнее", "Картинка"]} selected={setMakingSelectRow} data={making} />
              </Section>
              <Section title={makingSelectRow ? 'Редактирование' : 'Создание'}>
                <EquipmentsCreateComponents select={makingSelectRow} update={() => { getMaking(); setMakingSelectRow(null); }} />
              </Section>
          </Stack>
        )}

        {/* Группы оборудования */}
        {menuSelected === 6 && (
          <Stack spacing={3}>
              <Section
                title="Группы оборудования"
                actions={
                  <>
                    <Button startIcon={<RefreshIcon />} size="small" onClick={getEquipmentGroup}>Обновить</Button>
                    <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                  </>}
                loading={isLoading(equipmentsGroupRows)}
                empty={isEmpty(equipmentsGroupRows)}
              >
                <EquipmentsGroupTableComponents headers={["Название", "Компания"]} selected={setSelectEquipmentsGroupRows} data={equipmentsGroupRows} />
              </Section>
              <Section title={selectEquipmentsGroupRows ? 'Редактирование' : 'Создание'}>
                <EquipmentsGroupCreateComponents select={selectEquipmentsGroupRows} update={() => { setSelectEquipmentsGroupRows(null); getEquipmentGroup(); }} />
              </Section>
          </Stack>
        )}

        {/* Записи оборудования */}
        {menuSelected === 7 && (
          <Stack spacing={3}>
              <Section
                title="Записи оборудования"
                actions={
                  <>
                    <Button startIcon={<RefreshIcon />} size="small" onClick={getEquipmentsPosts}>Обновить</Button>
                    <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button></>}
                loading={isLoading(equipmentsPostRows)}
                empty={isEmpty(equipmentsPostRows)}
                height={240}
              >
                <EquipmentsPostsTableComponents headers={["Название", "Текст", "Описание", "Предварительная картинка", "Картинка", "Группа"]} selected={setSelectEquipmentsPostRows} data={equipmentsPostRows} />
              </Section>
              <Section title={selectEquipmentsPostRows ? 'Редактирование' : 'Создание'}>
                <EquipmentsPostsCreateComponents select={selectEquipmentsPostRows} update={() => { setSelectEquipmentsPostRows(null); getEquipmentsPosts(); }} />
              </Section>
          </Stack>
        )}

        {/* Документы оборудования */}
        {menuSelected === 8 && (
          <Stack spacing={3}>
              <Section
                title="Документы оборудования"
                actions={
                  <>
                    <Button startIcon={<RefreshIcon />} size="small" onClick={getDocuments}>Обновить</Button>
                    <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                  </>
                }
                loading={isLoading(documentsRows)}
                empty={isEmpty(documentsRows)}
                height={240}
              >
                <DocumentsTableComponents headers={["Название", "Ссылка"]} selected={setSelectDocumentsRows} data={documentsRows} />
              </Section>
              <Section title={selectDocumentsRows ? 'Редактирование' : 'Создание'}>
                <DocumentsCreateComponents select={selectDocumentsRows} update={() => { setSelectDocumentsRows(null); getDocuments(); }} />
              </Section>
          </Stack>
        )}

        {/* Запись ⇄ Документы */}
        {menuSelected === 9 && (
          <Stack spacing={3}>
              <Section
                title="Записи ⇄ Документы"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getDocumentsPost}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(documentsPostRows)}
                empty={isEmpty(documentsPostRows)}
                height={240}
              >
                <DocumentsPostTableComponents headers={["Запись", "Документ"]} selected={setSelectDocumentsPostRows} data={documentsPostRows} />
              </Section>
              <Section title={selectDocumentsPostRows ? 'Редактирование' : 'Создание'}>
                <DocumentsPostCreateComponents select={selectDocumentsPostRows} update={() => { setSelectDocumentsPostRows(null); getDocumentsPost(); }} />
              </Section>
          </Stack>
        )}

        {/* Решения */}
        {menuSelected === 2 && (
          <Stack spacing={3}>
              <Section
                title="Решения"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getSolutions}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(solutionsRows)}
                empty={isEmpty(solutionsRows)}
                height={240}
              >
                <SolutionsTableComponent headers={["Заголовок", "Текст", "Подробнее", "Картинка"]} selected={setSelectSolutionsRows} data={solutionsRows} />
              </Section>
              <Section title={selectSolutionsRows ? 'Редактирование' : 'Создание'}>
                <SolutionsCreateComponents select={selectSolutionsRows} update={() => { setSelectSolutionsRows(null); getSolutions(); }} />
              </Section>
          </Stack>
        )}

        {/* Решения ⇄ Документы */}
        {menuSelected === 11 && (
          <Stack spacing={3}>
              <Section
                title="Решения ⇄ Документы"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getDocumentsSolutionsPost}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(documentsSolutionsPostRows)}
                empty={isEmpty(documentsSolutionsPostRows)}
                height={240}
              >
                <DocumentsSolutionsPostTableComponents headers={["Запись", "Документ"]} selected={setSelectDocumentsSolutionsPostRows} data={documentsSolutionsPostRows} />
              </Section>
              <Section title={selectDocumentsSolutionsPostRows ? 'Редактирование' : 'Создание'}>
                <DocumentsSolutionsPostCreateComponents select={selectDocumentsSolutionsPostRows} update={() => { setSelectDocumentsSolutionsPostRows(null); getDocumentsSolutionsPost(); }} />
              </Section>
          </Stack>
        )}

        {/* О компании */}
        {menuSelected === 3 && (
          <Stack spacing={3}>
              <Section
                title="О компании"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getCompany}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(companyRows)}
                empty={isEmpty(companyRows)}
                height={240}
              >
                <CompanyTableComponent headers={["Заголовок", "Текст", "Подробнее", "Картинка"]} selected={setSelectCompanyRow} data={companyRows} />
              </Section>
              <Section title={selectCompanyRow ? 'Редактирование' : 'Создание'}>
                <CompanyCreateComponents select={selectCompanyRow} update={() => { getCompany(); setSelectCompanyRow(null); }} />
              </Section>
          </Stack>
        )}

        {/* Компания ⇄ Документы */}
        {menuSelected === 12 && (
          <Stack spacing={3}>
              <Section
                title="Компания ⇄ Документы"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getDocumentsCompanyPost}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(documentsCompanyPostRows)}
                empty={isEmpty(documentsCompanyPostRows)}
                height={240}
              >
                <DocumentsCompanyPostTableComponents headers={["Запись", "Документ"]} selected={setSelectDocumentsCompanyPostRows} data={documentsCompanyPostRows} />
              </Section>
              <Section title={selectDocumentsCompanyPostRows ? 'Редактирование' : 'Создание'}>
                <DocumentsCompanyPostCreateComponents select={selectDocumentsCompanyPostRows} update={() => { setSelectDocumentsCompanyPostRows(null); getDocumentsCompanyPost(); }} />
              </Section>
          </Stack>
        )}

        {/* Техническая поддержка */}
        {menuSelected === 4 && (
          <Stack spacing={3}>
              <Section
                title="Техническая поддержка"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getSupport}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(supportRows)}
                empty={isEmpty(supportRows)}
                height={240}
              >
                <SupportsTableComponent headers={["Заголовок", "Текст", "Подробнее", "Картинка"]} selected={setSelectSupportRow} data={supportRows} />
              </Section>
              <Section title={selectSupportRow ? 'Редактирование' : 'Создание'}>
                <SupportCreateComponents select={selectSupportRow} update={() => { getSupport(); setSelectSupportRow(null); }} />
              </Section>
          </Stack>
        )}

        {/* ТП ⇄ Документы */}
        {menuSelected === 13 && (
          <Stack spacing={3}>
              <Section
                title="ТП ⇄ Документы"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getDocumentsSupportsPost}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(documentsSupportsPostRows)}
                empty={isEmpty(documentsSupportsPostRows)}
                height={240}
              >
                <DocumentsSupportsPostTableComponents headers={["Запись", "Документ"]} selected={setSelectDocumentsSupportsPostRows} data={documentsSupportsPostRows} />
              </Section>
              <Section title={selectDocumentsSupportsPostRows ? 'Редактирование' : 'Создание'}>
                <DocumentsSupportsPostCreateComponents select={selectDocumentsSupportsPostRows} update={() => { setSelectDocumentsSupportsPostRows(null); getDocumentsSupportsPost(); }} />
              </Section>
          </Stack>
        )}

        {/* Группа референций */}
        {menuSelected === 5 && (
          <Stack spacing={3}>
              <Section
                title="Группы референций"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getReferenceGroup}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(referencesGroupRows)}
                empty={isEmpty(referencesGroupRows)}
                height={240}
              >
                <ReferenceGroupTableComponent headers={["Заголовок"]} selected={setSelectReferencesGroupRow} data={referencesGroupRows} />
              </Section>
              <Section title={selectReferencesGroupRow ? 'Редактирование' : 'Создание'}>
                <ReferenceGroupCreateComponents select={selectReferencesGroupRow} update={() => { getReferenceGroup(); setSelectReferencesGroupRow(null); }} />
              </Section>
          </Stack>
        )}

        {/* Записи референций */}
        {menuSelected === 10 && (
          <Stack spacing={3}>
              <Section
                title="Записи референций"
                actions={
                <>
                  <Button startIcon={<RefreshIcon />} size="small" onClick={getReferencePost}>Обновить</Button>
                  <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => scrollToBottom()}>Добавить</Button>
                </>}
                loading={isLoading(referencesPostRows)}
                empty={isEmpty(referencesPostRows)}
                height={240}
              >
                <ReferencePostTableComponents headers={["Название", "Текст", "Картинка", "Группа"]} selected={setSelectReferencesPostRow} data={referencesPostRows} />
              </Section>
              <Section title={selectReferencesPostRow ? 'Редактирование' : 'Создание'}>
                <ReferencePostCreateComponents select={selectReferencesPostRow} update={() => { setSelectReferencesPostRow(null); getReferencePost(); }} />
              </Section>
          </Stack>
        )}
        <div ref={bottomRef} />
      </AdminLayout>
    </ThemedApp>
  );
}
